import { Fragment, ReactNode } from "react";
import { Dialog, Transition, TransitionChild } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { cn } from "~/lib/utils";

// // // //

type Size =
    | "xs"
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl";

export interface Props {
    title?: string | ReactNode | null;
    // TODO - add "props.subtitle" here
    subtitle?: string | null;
    hideHeader?: boolean;
    size?: Size;
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
}

/**
A component that renders a right drawer panel with a title, subtitle, and user-provided content.
@param {string | null} props.title - The title of the drawer panel. If null, the close button will be positioned to the right.
@param {string | null} props.subtitle - The subtitle of the drawer panel.
@param {boolean | null} props.hideHeader - Whether or not to hide the header of the drawer. This will also hide the "close" button
@param {Size} [props.size="xl"] - The size of the drawer panel. One of "xs", "sm", "md", "lg", "xl", "2xl", "3xl", "4xl", "5xl", "6xl", "7xl".
@param {boolean} props.open - Whether the drawer panel is open or not.
@param {function} props.onClose - A callback function that will be called when the drawer panel is closed.
@param {React.ReactNode} props.children - The content to be displayed in the drawer panel.
@returns {JSX.Element} The rendered component.
*/
export function RightDrawer(props: Props) {
    const {
        open,
        onClose,
        size = "xl",
        title = null,
        hideHeader = false,
    } = props;

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-240"
                onClose={() => {
                    onClose();
                }}
            >
                <TransitionChild
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-slate-500/75 transition-opacity" />
                </TransitionChild>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                            <TransitionChild
                                as={Fragment}
                                enter="transform transition ease-in-out duration-300 sm:duration-300"
                                enterFrom="translate-x-full"
                                enterTo="translate-x-0"
                                leave="transform transition ease-in-out duration-300 sm:duration-300"
                                leaveFrom="translate-x-0"
                                leaveTo="translate-x-full"
                            >
                                <Dialog.Panel
                                    className={cn(
                                        "pointer-events-auto w-screen",
                                        {
                                            "max-w-xs": size === "xs",
                                            "max-w-sm": size === "sm",
                                            "max-w-md": size === "md",
                                            "max-w-lg": size === "lg",
                                            "max-w-xl": size === "xl",
                                            "max-w-2xl": size === "2xl",
                                            "max-w-3xl": size === "3xl",
                                            "max-w-4xl": size === "4xl",
                                            "max-w-5xl": size === "5xl",
                                            "max-w-6xl": size === "6xl",
                                            "max-w-7xl": size === "7xl",
                                        },
                                    )}
                                >
                                    <div className="flex h-full flex-col overflow-y-scroll bg-card pb-6 shadow-xl">
                                        {!hideHeader && (
                                            <div className="px-4 py-6 sm:px-6 mt-nav-height sticky top-nav-height z-100 bg-card border-b">
                                                <div
                                                    className={cn(
                                                        "flex items-center",
                                                        {
                                                            "justify-between":
                                                                title !== null,
                                                            "justify-end":
                                                                title === null,
                                                        },
                                                    )}
                                                >
                                                    {title && (
                                                        <Dialog.Title className="text-2xl leading-6 text-slate-700">
                                                            {title}
                                                        </Dialog.Title>
                                                    )}
                                                    <div className="ml-3 flex h-7 items-center">
                                                        <button
                                                            type="button"
                                                            className="rounded-md bg-card text-slate-400 hover:text-slate-500 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                                            onClick={() =>
                                                                onClose()
                                                            }
                                                        >
                                                            <span className="sr-only">
                                                                Close panel
                                                            </span>
                                                            <FontAwesomeIcon
                                                                icon={faTimes}
                                                                className="h-6 w-6"
                                                                aria-hidden="true"
                                                            />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* <div className="relative mt-6 flex-1 px-4 sm:px-6"> */}
                                        {/* Your content */}
                                        {/* </div> */}
                                        <div
                                            className={cn("relative flex-1", {
                                                "mt-nav-height": hideHeader,
                                            })}
                                        >
                                            {props.children}
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </TransitionChild>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
